import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildRemediationForFinding,
  formatRemediationLabel,
} from "../lib/remediationPlaybooks";

/**
 * RemediationFindingCard
 *
 * UI polish pass only:
 * - No logic changes
 * - No structural drift
 * - Spacing / visual rhythm only
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function kindToPill(kind) {
  const k = String(kind || "").toUpperCase();
  if (k === "UNSURE") return { label: "Unsure", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200" };
}

function categoryPill(category) {
  const c = String(category || "—");
  return { label: c, cls: "bg-slate-50 text-slate-700 border-slate-200" };
}

function gravityPill(gravity) {
  const g = Number(gravity || 0);
  const txt = g ? `Gravity ${g}/10` : "Gravity —";
  return { label: txt, cls: "bg-slate-50 text-slate-700 border-slate-200" };
}

function bandTone(band) {
  const b = String(band || "").toUpperCase();
  if (b === "CRITICAL") {
    return {
      wrap: "border-red-200 bg-red-50",
      eyebrow: "text-red-900",
      body: "text-red-950",
      label: "Immediate containment recommended",
    };
  }
  if (b === "HIGH") {
    return {
      wrap: "border-amber-200 bg-amber-50",
      eyebrow: "text-amber-900",
      body: "text-amber-950",
      label: "High-priority action set",
    };
  }
  if (b === "SIGNIFICANT") {
    return {
      wrap: "border-blue-200 bg-blue-50",
      eyebrow: "text-blue-900",
      body: "text-blue-950",
      label: "Priority action set",
    };
  }
  return {
    wrap: "border-emerald-200 bg-emerald-50",
    eyebrow: "text-emerald-900",
    body: "text-emerald-950",
    label: "Managed improvement actions",
  };
}

function buildFindingQuestionText(finding) {
  const t = String(finding?.text || "").trim();
  if (t) return t;
  const title = String(finding?.title || "").trim();
  return title || "—";
}

function normalizeStatus(s) {
  const v = String(s || "OPEN").toUpperCase();
  if (v === "OPEN" || v === "IN_PROGRESS" || v === "MITIGATED" || v === "CLOSED") return v;
  return "OPEN";
}

function chevronDownSvg(cls = "h-4 w-4") {
  return (
    <svg className={cls} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function chevronUpSvg(cls = "h-4 w-4") {
  return (
    <svg className={cls} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M14.77 12.79a.75.75 0 0 1-1.06-.02L10 9.06l-3.71 3.71a.75.75 0 1 1-1.06-1.06l4.24-4.24a.75.75 0 0 1 1.06 0l4.24 4.24a.75.75 0 0 1-.02 1.08Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function normalizeRegimes(regimes) {
  const list = safeArray(regimes).map(String).map((s) => s.trim()).filter(Boolean);
  const uniq = Array.from(new Set(list));
  return uniq.length ? uniq : ["NOT_SPECIFIED"];
}

function regimeLabel(key) {
  const k = String(key || "").toUpperCase();
  if (k === "NOT_SPECIFIED") return "Not specified";
  if (k === "US_FED") return "US (Federal)";
  if (k === "US_STATE") return "US (State)";
  return k;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function executiveStatusSummary(statusValue, gravityBandLabel) {
  const s = normalizeStatus(statusValue);
  if (s === "CLOSED") {
    return "This finding is marked closed. Preserve closure evidence, approval records, and validation artifacts so the control position remains defensible.";
  }
  if (s === "MITIGATED") {
    return "This finding is marked mitigated. Keep residual-risk evidence, monitoring proof, and any remaining follow-up actions together in the record.";
  }
  if (s === "IN_PROGRESS") {
    return gravityBandLabel
      ? `This finding is in active remediation and currently sits in the ${gravityBandLabel.toLowerCase()} urgency band. Finish the immediate actions below before broader control uplift.`
      : "This finding is in active remediation. Finish the immediate actions below before broader control uplift.";
  }
  return gravityBandLabel
    ? `This finding is currently in the ${gravityBandLabel.toLowerCase()} urgency band. Prioritize the first actions below before broader control uplift.`
    : "Prioritize the first actions below before broader control uplift.";
}

function dedupeStrings(list = []) {
  const seen = new Set();
  const out = [];
  safeArray(list).forEach((item) => {
    const value = String(item || "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

function inferPlaybookIdForFinding(findingLike = {}) {
  const finding = safeObj(findingLike);
  const idRaw = String(finding?.question_id ?? finding?.source_question_id ?? finding?.id ?? "");
  const numericQuestionId = Number(String(idRaw).replace(/[^0-9]/g, ""));
  const text = [finding?.title, finding?.text, finding?.guidance, finding?.category]
    .map((x) => String(x || "").toLowerCase())
    .join(" | ");

  const explicitMap = {
    1: "BIOMETRIC_IDENTIFICATION",
    2: "SOCIAL_SCORING",
    3: "BIOMETRIC_IDENTIFICATION",
    4: "MANIPULATIVE_DECEPTIVE_AI",
    5: "BIOMETRIC_IDENTIFICATION",
    6: "VULNERABLE_PERSONS",
    7: "SOCIAL_SCORING",
    8: "BIOMETRIC_IDENTIFICATION",
    9: "MANIPULATIVE_DECEPTIVE_AI",
    10: "TRANSPARENCY_DISCLOSURE",
    18: "DATA_PROTECTION_PRIVACY",
    21: "DATA_PROTECTION_PRIVACY",
    23: "MONITORING_DRIFT",
    24: "DATASET_BIAS",
    26: "MODEL_DOCUMENTATION",
    28: "SECURITY_ROBUSTNESS",
    29: "AI_RISK_MANAGEMENT",
    31: "TRANSPARENCY_DISCLOSURE",
    34: "EXPLAINABILITY",
    41: "MODEL_DOCUMENTATION",
    42: "CHANGE_MANAGEMENT",
    43: "RECORDKEEPING_RETENTION",
    44: "AUDIT_READINESS",
    45: "TRAINING_DATA_GOVERNANCE",
    46: "DATA_PROTECTION_PRIVACY",
    47: "USER_REDRESS",
    48: "SECURITY_ROBUSTNESS",
    49: "SECURITY_ROBUSTNESS",
    50: "SECURITY_ROBUSTNESS",
    51: "INCIDENT_REPORTING",
    52: "HUMAN_OVERSIGHT",
    53: "MODEL_TESTING_VALIDATION",
    54: "AI_RISK_MANAGEMENT",
    55: "TRANSPARENCY_DISCLOSURE",
    56: "USER_REDRESS",
    57: "THIRD_PARTY_VENDOR_RISK",
    58: "THIRD_PARTY_VENDOR_RISK",
    59: "MODEL_TESTING_VALIDATION",
    60: "SECURITY_ROBUSTNESS",
  };

  if (Number.isFinite(numericQuestionId) && explicitMap[numericQuestionId]) {
    return explicitMap[numericQuestionId];
  }

  if (text.includes("social scoring")) return "SOCIAL_SCORING";
  if (text.includes("biometric") || text.includes("facial") || text.includes("voiceprint")) return "BIOMETRIC_IDENTIFICATION";
  if (text.includes("dark pattern") || text.includes("deceptive") || text.includes("subliminal") || text.includes("manipulative")) {
    return "MANIPULATIVE_DECEPTIVE_AI";
  }
  if (text.includes("vulnerable") || text.includes("children") || text.includes("minors")) return "VULNERABLE_PERSONS";
  if (text.includes("privacy") || text.includes("data protection") || text.includes("dpia") || text.includes("ropa")) {
    return "DATA_PROTECTION_PRIVACY";
  }
  if (text.includes("human-in-the-loop") || text.includes("human override") || text.includes("employment") || text.includes("creditworthiness") || text.includes("medical")) {
    return "HUMAN_OVERSIGHT";
  }
  if (text.includes("monitoring") || text.includes("drift")) return "MONITORING_DRIFT";
  if (text.includes("security") || text.includes("prompt injection") || text.includes("weights") || text.includes("leakage")) {
    return "SECURITY_ROBUSTNESS";
  }
  if (text.includes("incident")) return "INCIDENT_REPORTING";
  if (text.includes("vendor") || text.includes("third-party") || text.includes("supplier")) return "THIRD_PARTY_VENDOR_RISK";
  if (text.includes("disclosure") || text.includes("notice") || text.includes("transparent") || text.includes("watermark")) {
    return "TRANSPARENCY_DISCLOSURE";
  }
  if (text.includes("explain")) return "EXPLAINABILITY";
  if (text.includes("appeal") || text.includes("complaint") || text.includes("redress")) return "USER_REDRESS";
  if (text.includes("documentation") || text.includes("model card") || text.includes("technical documentation")) {
    return "MODEL_DOCUMENTATION";
  }
  if (text.includes("change management")) return "CHANGE_MANAGEMENT";
  if (text.includes("record") || text.includes("retention")) return "RECORDKEEPING_RETENTION";
  if (text.includes("testing") || text.includes("validation") || text.includes("hallucination") || text.includes("accuracy")) {
    return "MODEL_TESTING_VALIDATION";
  }
  if (text.includes("training data") || text.includes("provenance") || text.includes("dataset")) {
    return text.includes("bias") ? "DATASET_BIAS" : "TRAINING_DATA_GOVERNANCE";
  }
  if (text.includes("inventory")) return "AI_SYSTEM_INVENTORY";
  if (text.includes("governance") || text.includes("risk management") || text.includes("nist")) return "AI_RISK_MANAGEMENT";

  return "GENERAL_COMPLIANCE";
}

function buildResilientRemediation({ finding, applicableJurisdictions }) {
  const f = safeObj(finding);
  const incoming = safeObj(f?.playbook);
  const jurisdictions = dedupeStrings(
    safeArray(f?.jurisdictions).length ? f?.jurisdictions : applicableJurisdictions
  );

  const shouldRebuild =
    !incoming?.playbookId ||
    !safeArray(incoming?.priorityActions).length ||
    !safeArray(incoming?.fullActions).length ||
    !safeArray(incoming?.legalChecklist).length ||
    !safeArray(incoming?.evidenceFocus).length;

  if (!shouldRebuild) {
    return {
      ...incoming,
      jurisdictions: jurisdictions.length ? jurisdictions : safeArray(incoming?.jurisdictions),
    };
  }

  return buildRemediationForFinding(
    {
      ...f,
      jurisdictions,
      playbook_id: f?.playbook_id || inferPlaybookIdForFinding(f),
    },
    {
      jurisdictions,
      gravity: f?.gravity,
    }
  );
}

function buildLegalDetailsModel({ finding, regimes, remediation }) {
  const f = safeObj(finding);
  const category = String(f?.category || "—");
  const title = String(f?.title || "—");
  const playbook = safeObj(remediation);
  const overlaySummaryLine = String(playbook?.overlaySummaryLine || "").trim();
  const legalOverlayNotes = safeArray(playbook?.legalNotes);
  const playbookLabel = String(playbook?.playbookLabel || "").toLowerCase();

  const baseEnforcementLens = (() => {
    const c = category.toLowerCase();
    if (c.includes("prohibited") || playbookLabel.includes("biometric") || playbookLabel.includes("social scoring") || playbookLabel.includes("manipulative")) {
      return "Potential heightened or prohibited-practice exposure; prioritize containment, documented cessation, leadership escalation, and evidence preservation.";
    }
    if (c.includes("high-risk") || playbookLabel.includes("human oversight") || playbookLabel.includes("risk management")) {
      return "Higher oversight expectations apply here; emphasize governance file completeness, reviewer checkpoints, approvals, and proof that controls operated in practice.";
    }
    if (playbookLabel.includes("privacy") || playbookLabel.includes("data protection")) {
      return "Privacy and rights exposure is central here; emphasize lawful-use rationale, notices, retention, access controls, and vendor/data-flow evidence.";
    }
    if (c.includes("transparency") || playbookLabel.includes("disclosure") || playbookLabel.includes("explainability")) {
      return "User-facing defensibility matters here; emphasize disclosures, explanation quality, complaint handling, and support scripts or screenshots.";
    }
    return "General compliance exposure remains relevant; prioritize complete evidence, accountable ownership, and proof that controls operate in practice.";
  })();

  return {
    header: `Legal Details — ${title}`,
    regimes: normalizeRegimes(regimes),
    enforcementLens: overlaySummaryLine
      ? `${baseEnforcementLens} ${overlaySummaryLine}`
      : baseEnforcementLens,
    checklist: safeArray(playbook?.legalChecklist).length
      ? safeArray(playbook.legalChecklist)
      : [
          "Document factual system behavior and scope (what, where, who affected).",
          "Capture change-control evidence (tickets, approvals, release notes).",
          "Preserve monitoring or testing artifacts (evaluations, drift, QA samples).",
          "Record remediation decision + owner + date + verification method.",
          "Store defensibility evidence (policies, training, notices, screenshots, logs).",
        ],
    evidenceFocus: safeArray(playbook?.evidenceFocus).length
      ? safeArray(playbook.evidenceFocus)
      : [
          "Policy / SOP reference (versioned)",
          "Owner attestation (role + date)",
          "Test results or monitoring screenshot",
          "User-facing disclosure / UX proof",
          "Vendor contract clause or DPA excerpt (if applicable)",
        ],
    note: (() => {
      const base = "This section is a structured legal-mode checklist (no statutory citations yet). Citations and precedent mapping can be layered later in a legal-tier expansion.";
      if (!legalOverlayNotes.length) return base;
      return `${base} Jurisdiction-specific emphasis: ${legalOverlayNotes.join(" ")}`;
    })(),
  };
}

export default function RemediationFindingCard({
  finding,
  entry,
  statuses,
  mode,
  onStatusChange,
  onNotesChange,
  onEvidenceAdd,
  onEvidenceChange,
  onEvidenceRemove,
  onUploadAdd,
  onUploadRemove,
  onUploadFile,
  onUploadOpen,
  applicableJurisdictions = [],
  layoutVariant = "ROW_C_FULL_PLAYBOOK",
  expandAllTick = 0,
  collapseAllTick = 0,
  defaultExpandLegalDetails = false,
}) {
  const f = safeObj(finding);
  const e = safeObj(entry);

  const fid = String(f?.id ?? "");
  const questionText = buildFindingQuestionText(f);

  const pillKind = kindToPill(f?.kind);
  const pillCategory = categoryPill(f?.category);
  const pillGravity = gravityPill(f?.gravity);

  const statusValue = normalizeStatus(e?.status || "OPEN");

  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (expandAllTick) setPlaybookOpen(true);
  }, [expandAllTick]);

  useEffect(() => {
    if (collapseAllTick) setPlaybookOpen(false);
  }, [collapseAllTick]);

  useEffect(() => {
    if (String(mode || "EXECUTIVE").toUpperCase() !== "LEGAL") {
      setLegalOpen(false);
      return;
    }
    if (defaultExpandLegalDetails) setLegalOpen(true);
  }, [mode, defaultExpandLegalDetails]);

  const effectiveRegimes = useMemo(() => {
    const hub = safeArray(applicableJurisdictions);
    if (hub.length) return hub;
    return safeArray(f?.jurisdictions);
  }, [applicableJurisdictions, f]);

  const remediation = useMemo(
    () => buildResilientRemediation({ finding: f, applicableJurisdictions: effectiveRegimes }),
    [f, effectiveRegimes]
  );

  const legalModel = useMemo(
    () => buildLegalDetailsModel({ finding: f, regimes: effectiveRegimes, remediation }),
    [f, effectiveRegimes, remediation]
  );

  const evidenceLinks = safeArray(e?.evidence_links);
  const uploads = safeArray(e?.uploads);

  const playbookText = String(remediation?.guidanceText || f?.guidance || "").trim();
  const showRapidActions = String(mode || "EXECUTIVE").toUpperCase() === "EXECUTIVE";
  const priorityActions = safeArray(remediation?.priorityActions);
  const fullActions = safeArray(remediation?.fullActions);
  const gravityBandLabel = String(remediation?.gravityBandLabel || "").trim();
  const gravityTone = bandTone(remediation?.gravityBand);
  const playbookLabel = formatRemediationLabel(remediation, f?.category);
  const overlaySummaryLine = String(remediation?.overlaySummaryLine || "").trim();
  const executiveSummaryText = executiveStatusSummary(statusValue, gravityBandLabel);

  const handleChooseFiles = async (ev) => {
    const files = Array.from(ev?.target?.files || []);
    if (!files.length || !onUploadFile) return;

    try {
      setUploadBusy(true);
      for (const file of files) {
        await onUploadFile(fid, file);
      }
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Finding</div>
            <div className="mt-2 text-sm font-black text-slate-900 whitespace-pre-line">{questionText}</div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillKind.cls)}>
              {pillKind.label}
            </div>
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillCategory.cls)}>
              {pillCategory.label}
            </div>
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillGravity.cls)}>
              {pillGravity.label}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Status</div>

            <div className="mt-4 flex flex-col gap-3">
              <select
                key={`status-${fid}`}
                id={`status-${fid}`}
                name={`status-${fid}`}
                data-finding-id={fid}
                value={statusValue}
                onChange={(ev) => onStatusChange?.(fid, normalizeStatus(ev.target.value))}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800"
              >
                {safeArray(statuses).map((s) => (
                  <option key={String(s?.value)} value={String(s?.value)}>
                    {String(s?.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                  How to Fix (Playbook)
                </div>
                <div className="mt-2 text-xs text-slate-500">Action guidance for this finding.</div>
              </div>

              <button
                type="button"
                onClick={() => setPlaybookOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-2xl border border-blue-600 bg-blue-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:border-blue-700 hover:bg-blue-700"
                title={playbookOpen ? "Collapse Playbook" : "Expand Playbook"}
              >
                {playbookOpen ? "Hide" : "Show"}
                {playbookOpen ? chevronUpSvg("h-4 w-4") : chevronDownSvg("h-4 w-4")}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
          {playbookOpen ? (
            <div className="space-y-6">
              {showRapidActions ? (
                <div className={cn("rounded-3xl border p-5", gravityTone.wrap)}>
                  <div className={cn("text-[11px] font-black uppercase tracking-[0.35em]", gravityTone.eyebrow)}>
                    {gravityTone.label}
                  </div>
                  <div className={cn("mt-2 text-sm", gravityTone.body)}>
                    {executiveSummaryText}
                  </div>

                  {priorityActions.length ? (
                    <ul className="mt-4 list-disc pl-5 text-sm text-slate-800 space-y-1">
                      {priorityActions.map((item, idx) => (
                        <li key={idx}>{String(item)}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-4 text-sm text-slate-700">Immediate priorities are not yet available for this finding.</div>
                  )}
                </div>
              ) : null}

              {playbookLabel ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Mapped Playbook</div>
                  <div className="mt-2 text-sm font-black text-slate-900">{playbookLabel}</div>
                  {remediation?.summary ? <div className="mt-2 text-sm text-slate-600">{remediation.summary}</div> : null}
                  {overlaySummaryLine ? <div className="mt-2 text-xs font-semibold text-slate-500">{overlaySummaryLine}</div> : null}
                </div>
              ) : null}

              {fullActions.length ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Full Remediation Controls</div>
                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-800 space-y-1">
                    {fullActions.map((item, idx) => (
                      <li key={idx}>{String(item)}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-800 whitespace-pre-line">
                  {playbookText || "No playbook guidance is available for this finding yet."}
                </div>
              )}

              {String(mode || "EXECUTIVE").toUpperCase() === "LEGAL" ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-5">
                  <button
                    type="button"
                    onClick={() => setLegalOpen((v) => !v)}
                    className={cn(
                      "w-full rounded-2xl border px-4 py-3 text-left",
                      legalOpen
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-900 hover:border-slate-400"
                    )}
                    title={legalOpen ? "Collapse Legal Details" : "Expand Legal Details"}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-black uppercase tracking-widest">Legal Details</div>
                        <div className="text-[11px] font-extrabold uppercase tracking-[0.3em] opacity-90">(Audit-Ready)</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-black uppercase tracking-widest">{legalOpen ? "Hide" : "Show"}</div>
                        {legalOpen ? chevronUpSvg("h-5 w-5") : chevronDownSvg("h-5 w-5")}
                      </div>
                    </div>
                  </button>

                  {legalOpen ? (
                    <div className="mt-5 space-y-5">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                          Applicable Regimes
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {safeArray(legalModel.regimes).map((r) => (
                            <div
                              key={String(r)}
                              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-700"
                              title={String(r)}
                            >
                              {regimeLabel(r)}
                            </div>
                          ))}
                        </div>
                        {safeArray(legalModel.regimes).includes("NOT_SPECIFIED") ? (
                          <div className="mt-3 text-xs text-slate-500">
                            No jurisdictions were present for this finding (neither hub jurisdictions nor finding.jurisdictions).
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                          Enforcement Lens
                        </div>
                        <div className="mt-2 text-sm text-slate-800">{legalModel.enforcementLens}</div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                            Documentation Checklist
                          </div>
                          <ul className="mt-3 list-disc pl-5 text-sm text-slate-800 space-y-1">
                            {safeArray(legalModel.checklist).map((x, idx) => (
                              <li key={idx}>{String(x)}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                            Evidence Focus
                          </div>
                          <ul className="mt-3 list-disc pl-5 text-sm text-slate-800 space-y-1">
                            {safeArray(legalModel.evidenceFocus).map((x, idx) => (
                              <li key={idx}>{String(x)}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="text-xs text-slate-500">{legalModel.note}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              Playbook is collapsed. Use <span className="font-black">Show</span> to expand.
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Notes</div>
            <textarea
              value={String(e?.notes || "")}
              onChange={(ev) => onNotesChange?.(fid, ev.target.value)}
              className="mt-4 h-28 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-slate-400"
              placeholder="Add internal notes, owner, target date, validation plan…"
            />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Evidence Links</div>
              <button
                type="button"
                onClick={() => onEvidenceAdd?.(fid)}
                className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-black uppercase tracking-widest text-white"
              >
                Add
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {evidenceLinks.length === 0 ? (
                <div className="text-sm text-slate-500">No evidence links yet.</div>
              ) : (
                evidenceLinks.map((row, idx) => {
                  const rid = String(row?.id || `e_${idx}`);
                  return (
                    <div key={rid} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="grid grid-cols-1 gap-2">
                        <input
                          value={String(row?.label || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, label: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="Label (e.g., Policy link, Ticket, Screenshot)"
                        />
                        <input
                          value={String(row?.url || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, url: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="URL"
                        />
                        <textarea
                          value={String(row?.notes || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, notes: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="Notes"
                        />
                        <button
                          type="button"
                          onClick={() => onEvidenceRemove?.(fid, rid)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Uploads</div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleChooseFiles}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadBusy}
                  className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-black uppercase tracking-widest text-white disabled:opacity-60"
                  title="Upload one or more evidence files to Supabase Storage"
                >
                  {uploadBusy ? "Uploading…" : "Upload File"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onUploadAdd?.(fid, {
                      id: `up_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                      filename: "example.pdf",
                      mime_type: "application/pdf",
                      size_bytes: 0,
                      last_modified_ms: Date.now(),
                      storage_state: "upload_ready",
                      storage_key: null,
                    })
                  }
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                  title="Adds upload-ready metadata (fallback placeholder)"
                >
                  Add Upload (Meta)
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {uploads.length === 0 ? (
                <div className="text-sm text-slate-500">No uploads yet.</div>
              ) : (
                uploads.map((u, idx) => {
                  const uid = String(u?.id || `u_${idx}`);
                  const canOpen = String(u?.storage_state || "") === "stored" && String(u?.storage_key || "");
                  return (
                    <div key={uid} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-sm font-black text-slate-900">{String(u?.filename || "File")}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {String(u?.mime_type || "—")} • {Number(u?.size_bytes || 0).toLocaleString("en-US")} bytes
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Storage: <span className="font-black">{String(u?.storage_state || "—")}</span>
                      </div>
                      {u?.uploaded_at_iso ? (
                        <div className="mt-1 text-xs text-slate-500">
                          Uploaded: <span className="font-black">{fmtDateTime(u.uploaded_at_iso)}</span>
                        </div>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {canOpen ? (
                          <button
                            type="button"
                            onClick={() => onUploadOpen?.(fid, uid)}
                            className="rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-700 border border-slate-200 hover:border-slate-300"
                          >
                            Open
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => onUploadRemove?.(fid, uid)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
      </div>
    </div>
  );
}
